'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Label } from '@/components/ui/label';
import { apiPatch } from '@/lib/api';
import { rosterInline } from '@/app/[locale]/event/[eventId]/libs/teamLabels';
import { TEAM_INFO_MAX_LENGTH, TEAM_NAME_MAX_LENGTH } from '../register/libs/teamRegistration';
import type { RegisteredGroup } from '../libs/useRegistrationStatus';

export type TeamFields = { team_name: string | null; team_info: string | null };

interface RegisteredTeamEntryProps {
  eventId: string;
  group: RegisteredGroup;
  /** Registration is open, so the player may still edit their own entry. */
  canEdit: boolean;
  /** The entry's status / payment state, shown beside its name (survey follow-ups 9 and 11). */
  stateLabel?: string;
  onSaved: (groupId: string, fields: TeamFields) => void;
}

const fieldClass =
  'border-border bg-background focus:border-primary focus:ring-primary/20 w-full rounded-md border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2';

/**
 * One team-division line in the "You're registered" card: the division and
 * team name, the roster underneath, and — while registration is open — an
 * inline editor for both. Saves through the player's own PATCH; the organiser
 * takes over from the registrants board once registration closes.
 */
export function RegisteredTeamEntry({
  eventId,
  group,
  canEdit,
  stateLabel,
  onSaved,
}: RegisteredTeamEntryProps) {
  const t = useTranslations('Event');
  const [editing, setEditing] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamInfo, setTeamInfo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const roster = rosterInline(group.team_info);

  const startEdit = () => {
    setTeamName(group.team_name ?? '');
    setTeamInfo(group.team_info ?? '');
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiPatch<{ success: boolean } & TeamFields>(
        `/api/event/${eventId}/participant-register`,
        { event_group_id: group.id, team_name: teamName, team_info: teamInfo },
      );
      onSaved(group.id, { team_name: res.team_name, team_info: res.team_info });
      setEditing(false);
    } catch (err) {
      const closed = err instanceof Error && err.message.includes('registration_closed');
      const withdrawn = (err as { code?: unknown } | null)?.code === 'registration_withdrawn';
      setError(
        t(
          withdrawn
            ? 'registration.teamEdit.withdrawn'
            : closed
              ? 'registration.teamEdit.locked'
              : 'registration.teamEdit.error',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    const nameId = `team_name_${group.id}`;
    const infoId = `team_info_${group.id}`;
    const canSave = teamInfo.trim().length > 0 && !saving;
    return (
      <li className="bg-card/60 rounded-md p-3 text-left">
        <div className="mb-2 text-sm font-medium text-green-700 dark:text-green-300">
          {group.name}
        </div>
        <div className="space-y-2">
          <div className="space-y-1">
            <Label htmlFor={nameId} className="text-xs font-medium tracking-wide uppercase">
              {t('registration.team.teamNameLabel')}
            </Label>
            <input
              id={nameId}
              type="text"
              maxLength={TEAM_NAME_MAX_LENGTH}
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder={t('registration.team.teamNamePlaceholder')}
              className={fieldClass}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={infoId} className="text-xs font-medium tracking-wide uppercase">
              {t('registration.team.teamInfoLabel')} *
            </Label>
            <textarea
              id={infoId}
              rows={3}
              maxLength={TEAM_INFO_MAX_LENGTH}
              value={teamInfo}
              onChange={(e) => setTeamInfo(e.target.value)}
              placeholder={t('registration.team.teamInfoPlaceholder')}
              className={fieldClass}
            />
          </div>
          {error && <p className="text-danger text-xs">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!canSave}
              onClick={save}
              className="rounded-md bg-green-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            >
              {t(saving ? 'registration.teamEdit.saving' : 'registration.teamEdit.save')}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => setEditing(false)}
              className="rounded-md border border-green-700/40 px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-300"
            >
              {t('registration.teamEdit.cancel')}
            </button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="text-sm font-medium text-green-700 dark:text-green-300">
      <div className="flex items-start justify-center gap-2">
        <span>{group.team_name ? `${group.name} · ${group.team_name}` : group.name}</span>
        {stateLabel && <span className="text-xs font-normal opacity-80">{stateLabel}</span>}
        {canEdit && (
          <button
            type="button"
            onClick={startEdit}
            className="text-xs font-normal underline underline-offset-2 hover:opacity-80"
          >
            {t('registration.teamEdit.edit')}
          </button>
        )}
      </div>
      {roster && (
        <div className="text-xs font-normal text-green-700/70 dark:text-green-300/70">
          {t('registration.yourTeam')}: {roster}
        </div>
      )}
    </li>
  );
}
